import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAccountClaims } from '../hooks/useAccountClaims';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { MANAGER_ROLES } from '../types';
import SubscriptionExpiredPage from './SubscriptionExpiredPage';

interface Props {
  children: React.ReactNode;
}

/**
 * Gates the workspace based on tenant subscription state.
 *
 * Rule (PO decision): when a tenant's subscription is not active, only
 * MANAGER_ROLES (admin tier + diretor/gestor) and account_owners may
 * continue using the platform. Operational roles (bdr, sdr, executive,
 * mkt, agent, member, ...) are routed to <SubscriptionExpiredPage> so
 * they ping their admin to regularise billing.
 *
 * Failure mode is fail-open: while loading, on transient errors, or when
 * the billing endpoint cannot be reached, the gate lets the user through.
 * Blocking on infra hiccups would lock everyone out the moment billing
 * is degraded — much worse than the rare case of letting an expired
 * tenant slip in for one session.
 */
export const SubscriptionGate: React.FC<Props> = ({ children }) => {
  const { user } = useAuth();
  const { accountRole } = useAccountClaims();
  const { data, isLoading, isError } = useSubscriptionStatus();

  if (isLoading || isError || !data) return <>{children}</>;
  if (data.active) return <>{children}</>;

  const managerRoles = MANAGER_ROLES as readonly string[];
  const primaryRole = user?.role ?? '';
  const extraRoles = user?.roles ?? [];
  const isManager =
    managerRoles.includes(primaryRole) ||
    extraRoles.some((r: string) => managerRoles.includes(r));
  const isAccountOwner = accountRole === 'account_owner';

  if (isManager || isAccountOwner) return <>{children}</>;

  return (
    <SubscriptionExpiredPage status={data.status} expiresAt={data.expiresAt} />
  );
};

export default SubscriptionGate;
